// Data Pipeline API Client
// Dedicated client for interacting with the Data Pipeline backend service

// Base URL configuration
const BASE_URL = import.meta.env.VITE_DATA_PIPELINE_API_URL;

// Typed interfaces matching API response models
export interface DEXPriceResponse {
  tokenAddress: string;
  price: number;
  volume24h: number;
  priceChange24h: number;
  liquidity: number;
  timestamp: number;
  source: 'jupiter' | 'raydium' | 'orca' | 'dexscreener';
}

export interface TokenMetricsResponse {
  address: string;
  price: number;
  marketCap: number;
  volume24h: number;
  priceChange24h: number;
  holders: number;
  liquidity: number;
  socialMetrics: {
    twitterFollowers?: number;
    discordMembers?: number;
    telegramMembers?: number;
  };
  riskMetrics: {
    rugPullRisk: number;
    honeypotRisk: number;
    auditStatus: 'verified' | 'pending' | 'failed' | 'unknown';
    liquidityLocked: boolean;
  };
  timestamp: number;
}

export interface MarketDataResponse {
  totalMarketCap: number;
  totalVolume24h: number;
  marketCapChange24h: number;
  topGainers: DEXPriceResponse[];
  topLosers: DEXPriceResponse[];
  trendingTokens: DEXPriceResponse[];
  timestamp: number;
}

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: number;
  services: {
    database: boolean;
    redis: boolean;
    kafka: boolean;
    apis: boolean;
  };
  uptime: number;
}

export interface CollectorHealthResponse {
  [collector: string]: {
    last_run: string | null;
    error_count: number;
    success_rate: number;
    status: string;
  };
}

export interface InfrastructureHealthResponse {
  [service: string]: {
    status: string;
    details: string;
  };
}

export interface CollectionMetricsResponse {
  solana_records_per_minute: number;
  token_records_per_minute: number;
  dex_records_per_minute: number;
  social_records_per_minute: number;
  market_records_per_minute: number;
  error_rate: number;
  api_latency_avg_ms: number;
  collector_success_rates: {
    [collector: string]: number;
  };
}

export interface WebSocketMessage {
  type: 'price_update' | 'token_metrics' | 'market_data';
  data: any;
  timestamp: number;
}

// Cache configuration
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();

  set<T>(key: string, data: T, ttlMs: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlMs
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// HTTP Client with retry logic
class HTTPClient {
  private cache = new RequestCache();

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useCache: boolean = true,
    cacheTtl: number = 5 * 60 * 1000,
    maxRetries: number = 3
  ): Promise<T> {
    const url = `${BASE_URL}${endpoint}`;
    const cacheKey = `${options.method || 'GET'}_${url}`;

    // Check cache first
    if (useCache && options.method !== 'POST') {
      const cached = this.cache.get<T>(cacheKey);
      if (cached) return cached;
    }

    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
          ...options,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Cache successful GET responses
        if (useCache && options.method !== 'POST') {
          this.cache.set(cacheKey, data, cacheTtl);
        }

        return data;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Request attempt ${attempt + 1} failed:`, error);

        if (attempt < maxRetries) {
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    throw lastError!;
  }

  clearCache(): void {
    this.cache.clear();
  }
}

// WebSocket Client for real-time data streams
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private subscribers = new Map<string, (message: WebSocketMessage) => void>();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isConnected = false;

  constructor(private endpoint: string = '/ws') {}

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = BASE_URL.replace(/^http/, 'ws') + this.endpoint;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Data Pipeline WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.notifySubscribers(message);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('Data Pipeline WebSocket disconnected:', event.code, event.reason);
        this.isConnected = false;
        this.stopHeartbeat();
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('Data Pipeline WebSocket error:', error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.attemptReconnect();
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max WebSocket reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      console.log(`Attempting WebSocket reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.connect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  private notifySubscribers(message: WebSocketMessage): void {
    for (const callback of this.subscribers.values()) {
      try {
        callback(message);
      } catch (error) {
        console.error('WebSocket subscriber callback error:', error);
      }
    }
  }

  subscribe(subscriberId: string, callback: (message: WebSocketMessage) => void): void {
    this.subscribers.set(subscriberId, callback);
    if (!this.isConnected) {
      this.connect();
    }
  }

  unsubscribe(subscriberId: string): void {
    this.subscribers.delete(subscriberId);
    if (this.subscribers.size === 0) {
      this.disconnect();
    }
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.subscribers.clear();
    this.isConnected = false;
  }

  get isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }
}

// Main Data Pipeline Client
export class DataPipelineClient {
  private httpClient = new HTTPClient();
  private wsClient = new WebSocketClient();
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private isHealthy = false;

  constructor() {
    this.startHealthMonitoring();
  }

  // DEX Prices API
  async getDEXPrices(): Promise<DEXPriceResponse[]> {
    return this.httpClient.request<DEXPriceResponse[]>('/dex/prices');
  }

  // Token Metrics API
  async getTokenMetrics(address: string): Promise<TokenMetricsResponse> {
    return this.httpClient.request<TokenMetricsResponse>(`/tokens/${address}/metrics`);
  }

  // Market Data API
  async getMarketData(): Promise<MarketDataResponse> {
    return this.httpClient.request<MarketDataResponse>('/market/data');
  }

  // Health Check API
  async getHealthStatus(): Promise<HealthCheckResponse> {
    try {
      const response = await this.httpClient.request<HealthCheckResponse>(
        '/health',
        {},
        false, // Don't cache health checks
        0
      );
      this.isHealthy = response.status === 'healthy';
      return response;
    } catch (error) {
      this.isHealthy = false;
      throw error;
    }
  }

  // Collector Health API
  async getCollectorHealth(): Promise<CollectorHealthResponse> {
    return this.httpClient.request<CollectorHealthResponse>(
      '/health/collectors',
      {},
      false, // Don't cache health checks
      0
    );
  }

  // Infrastructure Health API
  async getInfrastructureHealth(): Promise<InfrastructureHealthResponse> {
    return this.httpClient.request<InfrastructureHealthResponse>(
      '/health/infrastructure',
      {},
      false, // Don't cache health checks
      0
    );
  }

  // Collection Metrics API
  async getCollectionMetrics(): Promise<CollectionMetricsResponse> {
    return this.httpClient.request<CollectionMetricsResponse>(
      '/metrics/collection',
      {},
      false, // Don't cache metrics
      0
    );
  }

  // WebSocket subscriptions
  subscribeToPriceUpdates(subscriberId: string, callback: (message: WebSocketMessage) => void): void {
    this.wsClient.subscribe(subscriberId, callback);
  }

  unsubscribeFromPriceUpdates(subscriberId: string): void {
    this.wsClient.unsubscribe(subscriberId);
  }

  // Health monitoring
  private startHealthMonitoring(): void {
    // Check health every 30 seconds
    this.healthCheckInterval = setInterval(async () => {
      try {
        await this.getHealthStatus();
      } catch (error) {
        console.warn('Health check failed:', error);
      }
    }, 30000);
  }

  // Cleanup
  destroy(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.wsClient.disconnect();
    this.httpClient.clearCache();
  }

  // Status getters
  get isBackendHealthy(): boolean {
    return this.isHealthy;
  }

  get isWebSocketConnected(): boolean {
    return this.wsClient.isWebSocketConnected;
  }

  get cacheSize(): number {
    return this.httpClient['cache'].size();
  }

  clearCache(): void {
    this.httpClient.clearCache();
  }
}

// Export singleton instance
export const dataPipelineClient = new DataPipelineClient();